// =============================================================================
// sales-commercial-context.ts — Contrato Zod compartilhado do agregador de
// contexto comercial (`GET /api/sales/leads/:leadId/commercial-context` e
// `GET /api/conversations/:id/commercial-context`, F38-S01).
//
// Plano: docs/planejamento-livechat-contexto-comercial.md §2.3/§3-B/§4.
//
// Por que agregador, e não 4 requests reaproveitando os endpoints existentes
// (agenda, campanhas, tentativas, preferências): o painel do live chat abre a
// CADA troca de conversa — 4 round-trips com RBAC+flag avaliados 4× é
// desperdício mensurável, e o shape "por lead numa conversa" permite aplicar
// o escopo da conversa uma vez só (§2.3). O CRM consome o MESMO agregador
// pelo `leadId` (§3-B) — nenhum endpoint novo por consumidor.
//
// Formato do bloco (D3/blocos degradáveis, §2.3): cada bloco interno é
// gated por flag (`sales.appointments.enabled`/`sales.campaigns.enabled`) +
// permissão (`sales_appointments:read`/`sales_campaigns:read`) avaliada para
// o ator — SEM acesso, o bloco vem `null`/`[]`, NUNCA 404 da rota inteira
// (a rota só nega via `livechat.commercial_context.enabled`, a flag de
// composição). TODOS os blocos são nullable/opcionais — cache antigo do
// front não pode quebrar o parse (lição do drift `ref.kind`, doc 26 §22).
//
// `leadId` é nullable no shape (não o payload inteiro): quando a conversa não
// tem lead vinculado, a resposta é `{ leadId: null, nextAppointment: null,
// campaign: null, recentAttempts: [], restrictions: [] }` — um objeto válido
// sempre, nunca `null` no topo (menos um caso de borda pro front tratar).
//
// LGPD (doc 17 §8.3/§8.5): `reason` (restrições) e `notes`/`nextStep`
// (tentativas, via `ContactAttemptSchema`) são texto livre com PII
// potencial — já cobertos pela lista canônica de redact (`*.reason`/
// `*.notes`/`*.nextStep`/`*.next_step` em `apps/api/src/app.ts` e
// `apps/api/src/lib/logger.ts`, nenhum campo novo introduzido aqui).
// =============================================================================
import { z } from 'zod';
import { ContactAttemptSchema } from './sales-attempts.js';
import { CampaignMemberStatusSchema, ContactChannelSchema, ContactPreferenceStateSchema, SalesOutcomeCodeSchema, } from './sales.js';
// ---------------------------------------------------------------------------
// Bloco — próximo retorno agendado
// ---------------------------------------------------------------------------
export const CommercialContextNextAppointmentSchema = z.object({
    id: z.string().uuid(),
    /** ISO 8601 com offset — sempre futuro (compromissos vencidos não entram neste bloco). */
    scheduledAt: z.string(),
    channel: ContactChannelSchema,
    /** `agents.display_name` do responsável — nunca `users.id`/nome de usuário. */
    assignedAgentName: z.string(),
});
// ---------------------------------------------------------------------------
// Bloco — campanha ativa (membro na fila)
// ---------------------------------------------------------------------------
export const CommercialContextCampaignSchema = z.object({
    /** `sales_campaigns.id`. */
    id: z.string().uuid(),
    name: z.string(),
    /** Status do MEMBRO na fila desta campanha (`campaign_members.status`). */
    memberStatus: CampaignMemberStatusSchema,
    attemptCount: z.number().int().nonnegative(),
    lastOutcomeCode: SalesOutcomeCodeSchema.nullable(),
    /** ISO 8601 com offset — retorno combinado (`waiting_callback`). null se não aplicável. */
    nextActionAt: z.string().nullable(),
    /**
     * `users.id` de quem tem o membro sob posse (`status='claimed'`). Permite a
     * UI mostrar "em atendimento por X na campanha Y" (§2.2.1) — null quando o
     * membro não está reclamado por ninguém.
     */
    claimedByUserId: z.string().uuid().nullable(),
});
// ---------------------------------------------------------------------------
// Bloco — restrições de contato efetivas
// ---------------------------------------------------------------------------
export const CommercialContextRestrictionSchema = z.object({
    channel: ContactChannelSchema,
    /** Sempre `blocked` neste bloco — o endpoint só lista restrições EFETIVAS (§ query). */
    state: ContactPreferenceStateSchema,
    /** Texto livre — PII potencial (doc 17 §8.3). */
    reason: z.string().nullable(),
    /** ISO 8601 com offset. null = sem prazo definido. Bloqueios expirados NUNCA aparecem aqui. */
    expiresAt: z.string().nullable(),
});
// ---------------------------------------------------------------------------
// Payload agregado
// ---------------------------------------------------------------------------
export const CommercialContextSchema = z.object({
    /** null quando a conversa (rota por conversationId) não tem lead vinculado. */
    leadId: z.string().uuid().nullable(),
    /** null quando o compromisso não existe OU o bloco está indisponível para o ator. */
    nextAppointment: CommercialContextNextAppointmentSchema.nullable(),
    /** null quando o lead não está em campanha ativa OU o bloco está indisponível para o ator. */
    campaign: CommercialContextCampaignSchema.nullable(),
    /** Até 3 tentativas mais recentes, mais recente primeiro. [] quando o bloco está indisponível. */
    recentAttempts: z.array(ContactAttemptSchema).max(3),
    /** [] quando não há restrição efetiva OU o bloco está indisponível para o ator. */
    restrictions: z.array(CommercialContextRestrictionSchema),
});
//# sourceMappingURL=sales-commercial-context.js.map